import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageContext } from './AdminLayout';
import { FiFilter, FiRotateCw, FiSearch, FiX, FiRefreshCw } from "react-icons/fi";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format } from "date-fns";
import { es } from 'date-fns/locale';
import {
  getExportBookingDashboard,
  getExportBookingSummary,
  getTeamMembers,
  assignDriverToSlot,
  cancelSlot,
  getBookingInvoices,
  reconcileBooking,
  containerLoadedExport,
  submitPodExport
} from '../../ApiController/ApiService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import 'react-toastify/dist/ReactToastify.css';
import { useDashboard } from '../Context/Dashboardcontext';

const statusColors = {
  'In-Transit': 'bg-blue-500',
  'In Transit': 'bg-blue-500',
  'Delivered': 'bg-green-500',
  'Delayed': 'bg-red-500',
  'Pending': 'bg-yellow-400',
};

const getVal = (val) =>
  val !== null && val !== undefined && val !== '' && val !== 'null' ? val : '—';

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

const ExportContainer = () => {
  const { t } = useTranslation();
  const { selectedLanguage } = useContext(LanguageContext);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const dropdownRef = useRef(null);
  const dropdownRef2 = useRef(null);
  const calendarRef = useRef(null);
  const buttonRef = useRef(null);
  const buttonRef2 = useRef(null);
  const buttonRef3 = useRef(null);
  // Booking number search
  const [bookingSearch, setBookingSearch] = useState('');

  // View modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewShipmentData, setViewShipmentData] = useState(null);
  const [currentMongoId, setCurrentMongoId] = useState(null);
  const [viewModalLoading, setViewModalLoading] = useState(false);

  const [error, setError] = useState('');
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const { userProfile } = useDashboard();

  // Drivers for assignment
  const [drivers, setDrivers] = useState([]);
  const [assigningSlot, setAssigningSlot] = useState(null);
  const [selectedDriverForSlot, setSelectedDriverForSlot] = useState({});

  // Cancel slot
  const [cancellingSlot, setCancellingSlot] = useState(null);

  // Custom Confirm Modal State
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    slotId: null,
    type: '', // 'cancel', 'loaded', 'pod'
    showInput: false,
    inputLabel: '',
    inputPlaceholder: '',
    inputValue: ''
  });

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);

  // Reconcile
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await getTeamMembers();
        if (Array.isArray(res)) {
          setDrivers(res.filter(m => m.userType?.toLowerCase() === 'driver'));
        }
      } catch (err) {
        console.error("Failed to fetch drivers", err);
      }
    };
    fetchDrivers();
  }, []);

  // ─── VIEW SHIPMENT ─────────────────────────────────────────────
  const handleViewShipment = async (id) => {
    try {
      setViewModalLoading(true);
      setViewShipmentData(null);
      setSelectedDriverForSlot({});
      setInvoices([]);
      setShowInvoices(false);
      setShowViewModal(true);
      setCurrentMongoId(id);
      const res = await getExportBookingSummary(id);
      setViewShipmentData(res?.data || res);
    } catch (err) {
      toast.error('Failed to fetch shipment details');
      setShowViewModal(false);
    } finally {
      setViewModalLoading(false);
    }
  };

  const handleAssignDriver = async (slotId) => {
    const driverId = selectedDriverForSlot[slotId];
    if (!driverId) {
      toast.warning('Please select a driver first');
      return;
    }
    try {
      setAssigningSlot(slotId);
      await assignDriverToSlot(currentMongoId, slotId, { driverId });
      toast.success('Driver assigned to slot successfully');
      const res = await getExportBookingSummary(currentMongoId);
      setViewShipmentData(res?.data || res);
    } catch (err) {
      // Surface backend eligibility error messages directly
      const msg =
        err?.data?.message ||
        err?.message ||
        'Failed to assign driver';
      toast.error(msg);
    } finally {
      setAssigningSlot(null);
    }
  };

  const handleCancelSlot = (slotId) => {
    setCustomModal({
      isOpen: true,
      title: 'Are you sure?',
      message: 'Do you want to cancel this slot? This will free up the container number.',
      slotId,
      type: 'cancel',
      showInput: false,
      inputLabel: '',
      inputPlaceholder: '',
      inputValue: ''
    });
  };

  const handleContainerLoaded = (slotId) => {
    setCustomModal({
      isOpen: true,
      title: 'Container Loaded',
      message: 'Mark this container as loaded at the client location?',
      slotId,
      type: 'loaded',
      showInput: true,
      inputLabel: 'Seal Number (Optional)',
      inputPlaceholder: 'Enter seal number...',
      inputValue: ''
    });
  };

  const handleSubmitPod = (slotId) => {
    setCustomModal({
      isOpen: true,
      title: 'Submit POD',
      message: 'Submit Proof of Delivery for this container?',
      slotId,
      type: 'pod',
      showInput: true,
      inputLabel: 'Seal Number Correction (Optional)',
      inputPlaceholder: 'Enter seal number if missed earlier...',
      inputValue: ''
    });
  };

  const handleCustomModalSubmit = async () => {
    const { slotId, type, inputValue } = customModal;
    setCustomModal((prev) => ({ ...prev, isOpen: false }));
    
    if (type === 'cancel') {
      try {
        setCancellingSlot(slotId);
        await cancelSlot(currentMongoId, slotId);
        toast.success('Slot cancelled successfully');
        const res = await getExportBookingSummary(currentMongoId);
        setViewShipmentData(res?.data || res);
      } catch (err) {
        toast.error(err?.data?.message || err?.message || 'Failed to cancel slot');
      } finally {
        setCancellingSlot(null);
      }
    } else if (type === 'loaded') {
      try {
        await containerLoadedExport(currentMongoId, slotId, { sealNumber: inputValue });
        toast.success('Container marked as loaded');
        const res = await getExportBookingSummary(currentMongoId);
        setViewShipmentData(res?.data || res);
      } catch (err) {
        toast.error(err?.data?.message || err?.message || 'Failed to update');
      }
    } else if (type === 'pod') {
      try {
        await submitPodExport(currentMongoId, slotId, { sealNumber: inputValue });
        toast.success('POD submitted');
        const res = await getExportBookingSummary(currentMongoId);
        setViewShipmentData(res?.data || res);
      } catch (err) {
        toast.error(err?.data?.message || err?.message || 'Failed to submit POD');
      }
    }
  };

  const handleLoadInvoices = async () => {
    if (invoicesLoading) return;
    setInvoicesLoading(true);
    try {
      const res = await getBookingInvoices(currentMongoId);
      setInvoices(res?.data || res || []);
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleToggleInvoices = () => {
    const next = !showInvoices;
    setShowInvoices(next);
    if (next && invoices.length === 0) {
      handleLoadInvoices();
    }
  };

  const handleReconcile = async () => {
    if (reconciling) return;
    setReconciling(true);
    try {
      await reconcileBooking(currentMongoId);
      toast.success('Booking re-synced successfully');
      const res = await getExportBookingSummary(currentMongoId);
      setViewShipmentData(res?.data || res);
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'Re-sync failed');
    } finally {
      setReconciling(false);
    }
  };

  // ─── CLICK OUTSIDE ──────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target) && !buttonRef.current?.contains(e.target))
        setShowCalendar(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && !buttonRef2.current?.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (dropdownRef2.current && !dropdownRef2.current.contains(e.target) && !buttonRef3.current?.contains(e.target))
        setShowDropdown2(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleCalendarToggle = () => {
    setShowCalendar((prev) => !prev);
  };

  const toggleRegion = (region) =>
    setSelectedRegions((prev) => (prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region]));
  const toggleStatus = (status) =>
    setSelectedStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));

  const resetFilters = () => {
    setSelectedRegions([]);
    setSelectedStatuses([]);
    setSelectedDates([]);
    setBookingSearch('');
    setCurrentPage(1);
  };

  // ─── FETCH SHIPMENTS ────────────────────────────────────────────
  const fetchShipments = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 10 };
      if (selectedStatuses.length > 0) params.status = selectedStatuses;
      if (selectedRegions.length > 0) params.origin = selectedRegions;
      if (selectedDates.length > 0) params.startDate = selectedDates[0].toISOString().split('T')[0];
      if (bookingSearch.trim()) params.bookingNumber = bookingSearch.trim();
      const data = await getExportBookingDashboard(params);
      const responseData = data?.data || data;

      console.log("responseData 1444", responseData)
      let docs = responseData?.docs || [];

      // if (userProfile?.isAiPackageExpired === false) {
      //   docs = docs.filter((item) => item?.companyType === 'Dispax');
      // }

      setShipments(docs);
      setTotalDocs(responseData?.totalDocs || docs.length);
      setHasPrevPage(responseData?.hasPrevPage || false);
      setHasNextPage(responseData?.hasNextPage || false);
    } catch {
      setError(t('errors.fetchShipmentsFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
  }, [currentPage, selectedDates, selectedRegions, selectedStatuses, bookingSearch, userProfile?.isAiPackageExpired]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const data = await getExportBookingDashboard({ limit: 1000 });
      const responseData = data?.data || data;
      let docs = responseData?.docs || [];

      if (docs && docs.length > 0) {
        const origins = [...new Set(docs.map((s) => s.origin))];
        const destinations = [...new Set(docs.map((s) => s.destination))];
        setUniqueLocations([...new Set([...origins, ...destinations])].filter(Boolean));
        setUniqueStatuses([...new Set(docs.map((s) => s.status))].filter(Boolean));
      }
    };
    fetchFilterOptions();
  }, []);

  const handlePrevPage = () => { if (hasPrevPage) setCurrentPage((p) => p - 1); };
  const handleNextPage = () => { if (hasNextPage) setCurrentPage((p) => p + 1); };

  // ─── RENDER ─────────────────────────────────────────────────────
  return (
    <div className="text-white px-1 sm:px-4">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-xl font-semibold">{t('orders.title')}</h1>
      </div>

      {/* FILTERS */}
      <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
        <div className="flex flex-col sm:flex-row items-center bg-[#10194E] rounded-xl shadow-md w-full sm:w-auto">
          <div className="flex items-center gap-2 px-4 py-3 sm:py-5 border-b sm:border-b-0 sm:border-r border-[#1E2A60] w-full sm:w-auto">
            <FiFilter className="text-lg sm:text-base" />
            <span className="text-sm">{t('orders.filterBy')}</span>
          </div>

          {/* Date Filter */}
          <div className="relative w-full sm:w-auto">
            <button ref={buttonRef} onClick={handleCalendarToggle}
              className="px-4 py-3 sm:py-5 text-sm border-b sm:border-b-0 sm:border-r border-[#1E2A60] bg-transparent text-white w-full sm:w-auto text-left">
              {selectedDates.length > 0
                ? selectedDates.map((d) => format(d, 'PP', { locale: selectedLanguage === 'Spanish' ? es : undefined })).join(', ')
                : t('orders.date')}
            </button>
            {showCalendar && (
              <div ref={calendarRef} className="absolute left-0 p-4 shadow-lg rounded z-50"
                style={{ background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)' }}>
                <DayPicker mode="multiple" selected={selectedDates} onSelect={setSelectedDates}
                  locale={selectedLanguage === 'Spanish' ? es : undefined} />
              </div>
            )}
          </div>

          {/* Location Filter */}
          <div className="relative w-full sm:w-auto">
            <button ref={buttonRef2} onClick={() => setShowDropdown(!showDropdown)}
              className="bg-transparent text-white px-4 py-3 sm:py-5 text-sm border-b sm:border-b-0 sm:border-r border-[#1E2A60] outline-none w-full sm:w-auto text-left">
              {t('orders.location')}
            </button>
            {showDropdown && (
              <div ref={dropdownRef} style={{ background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)' }}
                className="absolute left-0 top-full mt-2 w-80 text-white rounded-2xl p-6 z-50 shadow-lg">
                <h2 className="text-lg font-semibold mb-4">{t('orders.location')}</h2>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {uniqueLocations.map((loc) => (
                    <button key={loc} onClick={() => toggleRegion(loc)}
                      className={`px-3 py-1 rounded-full border text-xs transition-all ${selectedRegions.includes(loc) ? 'bg-white text-black font-semibold' : 'bg-[#1D2A85] hover:bg-[#2A3AA5]'}`}>
                      {loc}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="relative w-full sm:w-auto">
            <button ref={buttonRef3} onClick={() => setShowDropdown2(!showDropdown2)}
              className="bg-transparent text-white px-4 py-3 sm:py-5 text-sm border-b sm:border-b-0 sm:border-r border-[#1E2A60] outline-none w-full sm:w-auto text-left">
              {t('orders.statusLabel')}
            </button>
            {showDropdown2 && (
              <div ref={dropdownRef2} style={{ background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)' }}
                className="absolute left-0 top-full mt-2 w-80 text-white rounded-2xl p-6 z-50 shadow-lg">
                <h2 className="text-lg font-semibold mb-4">{t('orders.selectOrderStatus')}</h2>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {uniqueStatuses.map((status) => (
                    <button key={status} onClick={() => toggleStatus(status)}
                      className={`px-3 py-1 rounded-full border text-xs transition-all ${selectedStatuses.includes(status) ? 'bg-white text-black font-semibold' : 'bg-[#1D2A85] hover:bg-[#2A3AA5]'}`}>
                      {t(`orders.status.${status.toLowerCase()}`, { defaultValue: status })}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button onClick={resetFilters} className="flex items-center gap-1 px-4 py-3 sm:py-5 text-sm text-orange-400 hover:underline">
            <FiRotateCw className="text-sm" /> {t('orders.resetFilter')}
          </button>
        </div>

        {/* Booking Number Search */}
        <div className="flex items-center bg-[#10194E] rounded-xl px-4 py-3 gap-2 w-full sm:w-72 border border-[#1E2A60]">
          <FiSearch className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={bookingSearch}
            onChange={(e) => { setBookingSearch(e.target.value); setCurrentPage(1); }}
            placeholder="Search by Booking No."
            className="bg-transparent text-white text-sm outline-none w-full placeholder-gray-400"
          />
          {bookingSearch && (
            <button onClick={() => { setBookingSearch(''); setCurrentPage(1); }} className="text-gray-400 hover:text-white">
              <FiX size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ─── MAIN TABLE ─────────────────────────────────────────── */}
      <div className="overflow-x-auto bg-[#1D2461] rounded-lg mt-4">
        <table className="w-full table-auto text-sm text-left min-w-[900px]">
          <thead className="bg-[#B2BAFF] text-[#202224] uppercase text-[12px]">
            <tr>
              <th className="p-3"></th>
              <th className="p-3">Booking No.</th>
              <th className="p-3">Port / Terminal</th>
              <th className="p-3">Container Type</th>
              <th className="p-3">Required</th>
              <th className="p-3">Wait</th>
              <th className="p-3">Assign</th>
              <th className="p-3">Collect</th>
              <th className="p-3">Transit</th>
              <th className="p-3">Done</th>
              <th className="p-3">Rem</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="p-6 text-center">
                  <div className="flex justify-center">
                    <div className="w-6 h-6 border-4 border-t-[#707EFF] border-[#2A2A4D] rounded-full animate-spin"></div>
                  </div>
                </td>
              </tr>
            ) : shipments.length > 0 ? (
              shipments.map((row) => (
                <tr key={row._id || row.bookingId} className="border-t border-[#2A2A4D] hover:bg-[#232d6b] transition-colors">
                  <td className="p-3"><input type="checkbox" /></td>
                  <td className="p-3 text-xs font-medium">{getVal(row.bookingNumber)}</td>
                  <td className="p-3 text-xs">{getVal(row.portName || row.port)} / {getVal(row.terminal)}</td>
                  <td className="p-3 text-xs">{getVal(row.containerType)}</td>
                  <td className="p-3 text-xs">{getVal(row.totalRequired)}</td>
                  <td className="p-3 text-xs font-semibold text-gray-300">{getVal(row.waiting)}</td>
                  <td className="p-3 text-xs font-semibold text-blue-300">{getVal(row.assigned)}</td>
                  <td className="p-3 text-xs font-semibold text-purple-400">{getVal(row.collected)}</td>
                  <td className="p-3 text-xs font-semibold text-orange-400">{getVal(row.inTransit)}</td>
                  <td className="p-3 text-xs font-semibold text-green-400">{getVal(row.completed)}</td>
                  <td className="p-3 text-xs font-semibold text-yellow-400">{getVal(row.remaining)}</td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`w-2 h-2 rounded-full ${statusColors[row.status] || 'bg-gray-500'}`}></span>
                      {t(`orders.status.${row.status?.toLowerCase()}`, { defaultValue: row.status })}
                    </span>
                  </td>
                  <td className="p-3">
                    <button onClick={() => handleViewShipment(row._id)}
                      className="bg-[#00B69B] text-white px-4 py-1 rounded-2xl hover:bg-teal-500 text-xs transition-colors whitespace-nowrap">
                      {t('orders.view')}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="p-4 text-center text-xs text-gray-400">{t('orders.noShipmentsFound')}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* PAGINATION */}
        <div className="flex justify-between items-center text-xs text-gray-400 p-4">
          <div>
            Showing {shipments.length > 0 ? (currentPage - 1) * 10 + 1 : 0}–{Math.min(currentPage * 10, totalDocs)} of {totalDocs}
          </div>
          <div className="flex gap-3">
            <button onClick={handlePrevPage} disabled={!hasPrevPage}
              className={`px-4 py-2 rounded-md text-xs ${hasPrevPage ? 'bg-[#00B69B] text-white hover:bg-teal-500' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}>
              {t('orders.previous')}
            </button>
            <button onClick={handleNextPage} disabled={!hasNextPage}
              className={`px-4 py-2 rounded-md text-xs ${hasNextPage ? 'bg-[#00B69B] text-white hover:bg-teal-500' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}>
              {t('orders.next')}
            </button>
          </div>
        </div>
      </div>

      {/* ─── VIEW MODAL ──────────────────────────────────────────── */}
      {showViewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="rounded-xl shadow-2xl text-white relative w-full max-w-2xl max-h-[90vh] overflow-y-auto hide-scrollbar"
            style={{ background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)' }}>

            <div className="flex justify-between items-start p-5 border-b border-[#2A2A4D] sticky top-0 z-10"
              style={{ background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)' }}>
              <div>
                <h2 className="text-lg font-bold">Shipment Details</h2>
                {viewShipmentData && (
                  <div className="flex flex-wrap gap-3 mt-1 text-xs">
                    <span className="text-gray-400">Internal ID: <span className="text-white font-semibold">{viewShipmentData.Id || viewShipmentData.bookingId}</span></span>
                    <span className="text-gray-400">Booking Ref: <span className="text-white font-semibold">{viewShipmentData.shippingBookingNumber || viewShipmentData.bookingNumber}</span></span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {viewShipmentData && (
                  <button
                    onClick={handleReconcile}
                    disabled={reconciling}
                    title="Re-sync booking counts from actual container data"
                    className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-cyan-400 border border-[#2A2A4D] hover:border-cyan-800 px-2 py-1 rounded transition disabled:opacity-40"
                  >
                    <FiRefreshCw size={11} className={reconciling ? 'animate-spin' : ''} />
                    {reconciling ? 'Syncing...' : 'Re-sync'}
                  </button>
                )}
                <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-white transition">
                  <FiX size={22} />
                </button>
              </div>
            </div>

            {viewModalLoading ? (
              <div className="flex justify-center items-center p-12">
                <div className="w-8 h-8 border-4 border-t-[#707EFF] border-[#2A2A4D] rounded-full animate-spin"></div>
              </div>
            ) : viewShipmentData ? (
              <div className="p-5 space-y-6">

                {/* Posting Client & Delivery Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {viewShipmentData.customerDetails && (
                    <div className="bg-[#162045] rounded-lg p-4 border border-[#2A2A4D]">
                      <p className="text-[11px] font-semibold text-cyan-400 uppercase tracking-wider mb-2">Customer Info</p>
                      <div className="text-xs space-y-1 text-gray-300">
                        <p><span className="text-gray-500">Company:</span> {getVal(viewShipmentData.customerDetails.customerCompanyName)}</p>
                        <p><span className="text-gray-500">Contact:</span> {getVal(viewShipmentData.customerDetails.customerContactName)}</p>
                        <p><span className="text-gray-500">Email:</span> {getVal(viewShipmentData.customerDetails.customerEmail)}</p>
                        <p><span className="text-gray-500">Address:</span> {getVal(viewShipmentData.customerDetails.customerAddress)}</p>
                      </div>
                    </div>
                  )}
                  {viewShipmentData.deliveryDetails && (
                    <div className="bg-[#162045] rounded-lg p-4 border border-[#2A2A4D]">
                      <p className="text-[11px] font-semibold text-cyan-400 uppercase tracking-wider mb-2">Delivery Details</p>
                      <div className="text-xs space-y-1 text-gray-300">
                        <p><span className="text-gray-500">Receiver:</span> {getVal(viewShipmentData.deliveryDetails.receiverCompanyName)}</p>
                        <p><span className="text-gray-500">Contact:</span> {getVal(viewShipmentData.deliveryDetails.contactPerson)}</p>
                        <p><span className="text-gray-500">Address:</span> {getVal(viewShipmentData.deliveryDetails.address)}</p>
                        <p><span className="text-gray-500">Apt Date:</span> {fmtDate(viewShipmentData.deliveryDetails.appointmentDate)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Booking Progress — visual segmented bar */}
                <div>
                  <p className="text-[11px] font-semibold text-cyan-400 uppercase tracking-wider mb-3">Booking Progress</p>
                  {/* Segmented progress bar */}
                  {(() => {
                    const total = viewShipmentData.totalRequired || viewShipmentData.slots?.length || 1;
                    const done = viewShipmentData.completed || 0;
                    const transit = viewShipmentData.inTransit || 0;
                    const collected = viewShipmentData.collected || 0;
                    const assigned = viewShipmentData.assigned || 0;
                    const waiting = viewShipmentData.waiting || 0;
                    const pct = (n) => `${Math.round((n / total) * 100)}%`;
                    return (
                      <div className="mb-3">
                        <div className="flex h-3 rounded-full overflow-hidden bg-[#0f1530] gap-px">
                          {done > 0 && <div style={{ width: pct(done) }} className="bg-green-500 transition-all" title={`Completed: ${done}`} />}
                          {transit > 0 && <div style={{ width: pct(transit) }} className="bg-orange-400 transition-all" title={`In Transit: ${transit}`} />}
                          {collected > 0 && <div style={{ width: pct(collected) }} className="bg-purple-500 transition-all" title={`Collected: ${collected}`} />}
                          {assigned > 0 && <div style={{ width: pct(assigned) }} className="bg-blue-500 transition-all" title={`Assigned: ${assigned}`} />}
                          {waiting > 0 && <div style={{ width: pct(waiting) }} className="bg-gray-600 transition-all" title={`Waiting: ${waiting}`} />}
                        </div>
                        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                          <span>{done}/{total} completed</span>
                          <span>{Math.round((done / total) * 100)}%</span>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Stat pills */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Waiting', val: viewShipmentData.waiting, color: 'text-gray-300 bg-gray-800' },
                      { label: 'Assigned', val: viewShipmentData.assigned, color: 'text-blue-300 bg-blue-900/40' },
                      { label: 'Collected', val: viewShipmentData.collected, color: 'text-purple-300 bg-purple-900/40' },
                      { label: 'In Transit', val: viewShipmentData.inTransit, color: 'text-orange-300 bg-orange-900/40' },
                      { label: 'Completed', val: viewShipmentData.completed, color: 'text-green-300 bg-green-900/40' },
                      { label: 'Remaining', val: viewShipmentData.remaining, color: 'text-yellow-300 bg-yellow-900/30' },
                    ].map(({ label, val, color }) => (
                      <span key={label} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${color}`}>
                        {label}: {val ?? 0}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Slots — with lifecycle stepper */}
                <div>
                  <p className="text-[11px] font-semibold text-cyan-400 uppercase tracking-wider mb-3">Container Slots</p>
                  {viewShipmentData.slots && viewShipmentData.slots.length > 0 ? (
                    <div className="space-y-3">
                      {viewShipmentData.slots.map((slot, idx) => {
                        // Map backend status → step index (0-based)
                        const STEPS = [
                          { key: 'Waiting',                    label: 'Wait',      ts: null },
                          { key: 'Assigned',                   label: 'Assigned',  ts: null },
                          { key: 'ArrivedAtEmptyPickup',       label: 'At Pickup', ts: slot.arrivedAtEmptyPickupAt },
                          { key: 'EmptyContainerCollected',    label: 'Collected', ts: slot.emptyContainerCollectedAt },
                          { key: 'ArrivedAtClientLoading',     label: 'At Client', ts: slot.arrivedAtClientLoadingAt },
                          { key: 'ContainerLoaded',            label: 'Loaded',    ts: slot.containerLoadedAt },
                          { key: 'PODSubmitted',               label: 'POD',       ts: slot.podSubmittedAt || slot.podAt },
                          { key: 'ArrivedAtEmptyReturn',       label: 'At Return', ts: slot.arrivedAtEmptyReturnAt },
                          { key: 'Completed',                  label: 'Done ✓',    ts: slot.completedAt || slot.gateInReturnedAt },
                        ];
                        const isCancelled = slot.status === 'Cancelled';
                        const currentStepIdx = isCancelled ? -1 : STEPS.findIndex(s => s.key === slot.status);
                        const activeIdx = currentStepIdx === -1 ? 0 : currentStepIdx;

                        return (
                          <div key={idx} className={`rounded-lg p-4 border ${isCancelled ? 'bg-red-950/20 border-red-900/40' : 'bg-[#162045] border-[#2A2A4D]'}`}>
                            {/* Slot header */}
                            <div className="flex justify-between items-center mb-3 border-b border-[#2A2A4D] pb-2">
                              <div className="flex items-center gap-3">
                                <h4 className="font-semibold text-sm">Slot {idx + 1}</h4>
                                <span className="text-[10px] text-gray-500">{slot.slotId}</span>
                              </div>
                              <span className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${
                                isCancelled ? 'bg-red-900 text-red-300' :
                                slot.status === 'Waiting' ? 'bg-gray-800 text-gray-300' :
                                slot.status === 'Assigned' ? 'bg-blue-900 text-blue-300' :
                                slot.status === 'Completed' ? 'bg-green-900 text-green-300' :
                                'bg-orange-900/60 text-orange-300'
                              }`}>
                                {slot.status || 'Waiting'}
                              </span>
                            </div>

                            {/* ── Lifecycle Stepper ── */}
                            {!isCancelled && (
                              <div className="mb-4 overflow-x-auto">
                                <div className="flex items-center min-w-max gap-0">
                                  {STEPS.map((step, si) => {
                                    const done = si < activeIdx;
                                    const active = si === activeIdx;
                                    return (
                                      <div key={si} className="flex items-center">
                                        <div className="flex flex-col items-center" style={{ minWidth: 48 }}>
                                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-2 ${
                                            done   ? 'bg-green-500 border-green-500 text-white' :
                                            active ? 'bg-[#6F1AFF] border-[#6F1AFF] text-white animate-pulse' :
                                                     'bg-[#0f1530] border-[#2A2A4D] text-gray-500'
                                          }`}>
                                            {done ? '✓' : si + 1}
                                          </div>
                                          <span className={`text-[8px] mt-0.5 text-center leading-tight ${
                                            done ? 'text-green-400' : active ? 'text-purple-300' : 'text-gray-600'
                                          }`}>
                                            {step.label}
                                          </span>
                                          {step.ts && (
                                            <span className="text-[7px] text-gray-600 mt-0.5">
                                              {new Date(step.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                          )}
                                        </div>
                                        {si < STEPS.length - 1 && (
                                          <div className={`h-px w-4 flex-shrink-0 ${
                                            si < activeIdx ? 'bg-green-500' : 'bg-[#2A2A4D]'
                                          }`} />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {isCancelled && (
                              <p className="text-xs text-red-400 mb-3 italic">This slot was cancelled — container number is available for reuse.</p>
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-2 text-xs">
                            {/* Driver Assignment for Waiting */}
                            {slot.status === 'Waiting' && (
                              <div className="col-span-full bg-[#101533] p-3 rounded-lg border border-[#2A2A4D]">
                                <div className="flex items-end gap-2">
                                  <div className="flex-1">
                                    <label className="text-gray-400 uppercase text-[9px] mb-1 block">Assign Driver</label>
                                    <select
                                      className="w-full bg-[#1D2461] border border-[#2A2A4D] rounded px-2 py-1.5 text-xs outline-none"
                                      value={selectedDriverForSlot[slot.slotId] || ''}
                                      onChange={(e) => setSelectedDriverForSlot({ ...selectedDriverForSlot, [slot.slotId]: e.target.value })}
                                    >
                                      <option value="">Select Driver...</option>
                                      {drivers.map(d => (
                                        <option key={d._id} value={d._id}>{d.fullName}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <button
                                    onClick={() => handleAssignDriver(slot.slotId)}
                                    disabled={assigningSlot === slot.slotId}
                                    className="bg-[#6F1AFF] hover:bg-[#8536FF] text-white px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-50 whitespace-nowrap"
                                  >
                                    {assigningSlot === slot.slotId ? '...' : 'Assign'}
                                  </button>
                                </div>
                                {/* Cancel slot button — only for Waiting */}
                                <div className="mt-2 flex justify-end">
                                  <button
                                    onClick={() => handleCancelSlot(slot.slotId)}
                                    disabled={cancellingSlot === slot.slotId}
                                    className="text-[10px] text-red-400 border border-red-800 hover:bg-red-900/30 px-3 py-1 rounded transition disabled:opacity-50"
                                  >
                                    {cancellingSlot === slot.slotId ? 'Cancelling...' : 'Cancel Slot'}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Show Assigned Driver Info */}
                            {slot.status !== 'Waiting' && (slot.driverId || slot.assignedDriverId) && (() => {
                              const driverObj = slot.driverId || slot.assignedDriverId;
                              return (
                                <div className="col-span-full sm:col-span-2 bg-[#101533] p-3 rounded-lg border border-[#2A2A4D]">
                                  <p className="text-gray-400 uppercase text-[9px] mb-1 block">Assigned Driver</p>
                                  <div className="flex gap-3 items-center">
                                    {driverObj.image && <img src={driverObj.image} alt="avatar" className="w-8 h-8 rounded-full object-cover" />}
                                    <div>
                                      <p className="font-semibold text-sm">{driverObj.fullName || driverObj.name || 'Assigned Driver'}</p>
                                      <p className="text-[10px] text-gray-400">{driverObj.email} {driverObj.phoneNumber || driverObj.mobileNumber ? `• ${driverObj.phoneNumber || driverObj.mobileNumber}` : ''}</p>
                                    </div>
                                  </div>
                                  
                                  {/* Manual override buttons */}
                                  <div className="mt-3 flex justify-end gap-2">
                                    {slot.status === 'ArrivedAtClient' && (
                                      <button
                                        onClick={() => handleContainerLoaded(slot.slotId)}
                                        className="text-[10px] text-blue-400 border border-blue-800 hover:bg-blue-900/30 px-3 py-1 rounded transition"
                                      >
                                        Mark as Loaded
                                      </button>
                                    )}
                                    {slot.status === 'ArrivedAtDelivery' && (
                                      <button
                                        onClick={() => handleSubmitPod(slot.slotId)}
                                        className="text-[10px] text-purple-400 border border-purple-800 hover:bg-purple-900/30 px-3 py-1 rounded transition"
                                      >
                                        Submit POD
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                            <div>
                              <p className="text-gray-400 uppercase text-[9px] mb-1">Container Number</p>
                              <p className="font-medium text-[13px]">{['Waiting', 'Assigned', 'ArrivedAtPort'].includes(slot.status) ? <span className="italic text-gray-500">Waiting for pickup</span> : getVal(slot.containerNumber)}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 uppercase text-[9px] mb-1">Chassis Number</p>
                              <p className="font-medium text-[13px]">{['Waiting', 'Assigned', 'ArrivedAtPort'].includes(slot.status) ? '—' : getVal(slot.chassisNumber)}</p>
                            </div>
                            
                            {/* Seal Number — displayed once set by driver */}
                            <div className="col-span-full sm:col-span-2">
                              <p className="text-gray-400 uppercase text-[9px] mb-1">Seal Number</p>
                              {slot.sealNumber ? (
                                <p className="font-semibold text-[13px] text-cyan-300 tracking-wide">{slot.sealNumber}</p>
                              ) : (
                                <p className="italic text-gray-500 text-[11px]">Not yet recorded</p>
                              )}
                            </div>

                            {/* Timestamps */}
                            <div>
                              <p className="text-gray-400 uppercase text-[9px] mb-1">Port Arrived</p>
                              <p className="font-medium text-[11px]">{slot.arrivedAtPortAt ? new Date(slot.arrivedAtPortAt).toLocaleString() : '—'}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 uppercase text-[9px] mb-1">Port Exited</p>
                              <p className="font-medium text-[11px]">{slot.portExitedAt ? new Date(slot.portExitedAt).toLocaleString() : '—'}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 uppercase text-[9px] mb-1">Delivery Arrived</p>
                              <p className="font-medium text-[11px]">{slot.arrivedAtDeliveryAt ? new Date(slot.arrivedAtDeliveryAt).toLocaleString() : '—'}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 uppercase text-[9px] mb-1">Completed At</p>
                              <p className="font-medium text-[11px] text-green-400">{slot.completedAt ? new Date(slot.completedAt).toLocaleString() : '—'}</p>
                            </div>

                            <div className="col-span-full">
                              <p className="text-gray-400 uppercase text-[9px] mb-1">Damage Report</p>
                              <p className="font-medium text-xs">{slot.damageReported ? <span className="text-red-400">{getVal(slot.damageReport)}</span> : 'None reported'}</p>
                            </div>
                            
                            {/* POD details */}
                            {slot.receiverName && (
                              <div className="col-span-full bg-[#101533] p-3 rounded-lg border border-[#2A2A4D] mt-2">
                                <p className="text-[11px] font-semibold text-green-400 uppercase tracking-wider mb-2">Proof of Delivery</p>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-gray-400 uppercase text-[9px] mb-1">Receiver Name</p>
                                    <p className="font-medium">{slot.receiverName}</p>
                                  </div>
                                  {slot.receiverSignature && (
                                    <div>
                                      <p className="text-gray-400 uppercase text-[9px] mb-1">Signature</p>
                                      <a href={slot.receiverSignature} target="_blank" rel="noopener noreferrer">
                                        <img src={slot.receiverSignature} alt="signature" className="h-10 bg-white rounded px-2" />
                                      </a>
                                    </div>
                                  )}
                                  {slot.podImages && slot.podImages.length > 0 && (
                                    <div className="col-span-2 mt-2">
                                      <p className="text-gray-400 uppercase text-[9px] mb-2">POD Images</p>
                                      <div className="flex gap-2 flex-wrap">
                                        {slot.podImages.map((img, i) => (
                                          <a key={i} href={img} target="_blank" rel="noopener noreferrer">
                                            <img src={img} alt={`pod-${i}`} className="w-16 h-16 object-cover rounded border border-[#2A2A4D] hover:opacity-80" />
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* General Photos */}
                            {slot.photos && slot.photos.length > 0 && (
                              <div className="col-span-full mt-2">
                                <p className="text-gray-400 uppercase text-[9px] mb-2">Photos</p>
                                <div className="flex gap-2 flex-wrap">
                                  {slot.photos.map((img, i) => (
                                    <a key={i} href={img} target="_blank" rel="noopener noreferrer">
                                      <img src={img} alt={`slot-${idx}-photo-${i}`} className="w-16 h-16 object-cover rounded border border-[#2A2A4D] hover:opacity-80" />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No slots found.</p>
                  )}
                </div>

                {/* ─── INVOICES SECTION ────────────────────────────── */}
                <div className="border border-[#2A2A4D] rounded-lg overflow-hidden">
                  <button
                    onClick={handleToggleInvoices}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#162045] hover:bg-[#1a2860] transition text-sm font-semibold text-cyan-400"
                  >
                    <span className="uppercase tracking-wider text-[11px]">Invoices</span>
                    <span className="text-xs text-gray-400">{showInvoices ? '▲ Hide' : '▼ Show'}</span>
                  </button>

                  {showInvoices && (
                    <div className="p-4 bg-[#0f1530]">
                      {invoicesLoading ? (
                        <div className="flex justify-center py-4">
                          <div className="w-5 h-5 border-2 border-t-[#707EFF] border-[#2A2A4D] rounded-full animate-spin" />
                        </div>
                      ) : invoices.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs text-left">
                            <thead>
                              <tr className="text-gray-400 uppercase text-[10px] border-b border-[#2A2A4D]">
                                <th className="pb-2 pr-4">Invoice #</th>
                                <th className="pb-2 pr-4">Amount</th>
                                <th className="pb-2 pr-4">Status</th>
                                <th className="pb-2">PDF</th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoices.map((inv, i) => (
                                <tr key={i} className="border-b border-[#1a1f4a] last:border-0">
                                  <td className="py-2 pr-4 font-medium text-white">{inv.invoiceNumber || '—'}</td>
                                  <td className="py-2 pr-4 text-green-300">${inv.amount != null ? Number(inv.amount).toLocaleString() : '—'}</td>
                                  <td className="py-2 pr-4">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                      inv.status === 'Paid' ? 'bg-green-900 text-green-300' :
                                      inv.status === 'Pending' ? 'bg-yellow-900 text-yellow-300' :
                                      'bg-gray-800 text-gray-300'
                                    }`}>
                                      {inv.status || 'Unknown'}
                                    </span>
                                  </td>
                                  <td className="py-2">
                                    {inv.pdfPath ? (
                                      <a
                                        href={inv.pdfPath}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:text-blue-300 underline"
                                      >
                                        ↓ Download
                                      </a>
                                    ) : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-3">No invoices generated yet.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="pb-2 pt-4">
                  <button onClick={() => setShowViewModal(false)}
                    className="w-full py-2.5 bg-[#1D2461] border border-[#2A2A4D] text-white rounded-xl hover:bg-[#2A3AA5] transition font-medium text-sm">
                    Close
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Custom Confirm Modal */}
      {customModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center backdrop-blur-sm">
          <div className="bg-[#1D2461] border border-[#2A2A4D] rounded-xl shadow-2xl p-6 w-[90%] max-w-sm transform transition-all">
            <h3 className="text-xl font-bold text-white mb-2">{customModal.title}</h3>
            <p className="text-gray-300 text-sm mb-4">{customModal.message}</p>
            
            {customModal.showInput && (
              <div className="mb-4">
                <label className="text-xs text-gray-400 block mb-1 uppercase tracking-wider">{customModal.inputLabel}</label>
                <input
                  type="text"
                  className="w-full bg-[#101533] border border-[#2A2A4D] text-white px-3 py-2 rounded-lg text-sm outline-none focus:border-[#6F1AFF] transition"
                  placeholder={customModal.inputPlaceholder}
                  value={customModal.inputValue}
                  onChange={(e) => setCustomModal({ ...customModal, inputValue: e.target.value })}
                />
              </div>
            )}
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setCustomModal({ ...customModal, isOpen: false })}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCustomModalSubmit}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition ${customModal.type === 'cancel' ? 'bg-red-500 hover:bg-red-600' : 'bg-[#6F1AFF] hover:bg-[#8536FF]'}`}
              >
                {customModal.type === 'cancel' ? 'Yes, cancel it' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExportContainer;